<template>
  <v-stepper-content :step="stepNumber">
    <v-card flat class="mb-4">
      <v-card-text>
        <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-file-document-outline</v-icon>
          Documentación del vehículo
          <span class="caption grey--text ml-2">(opcional)</span>
        </v-card-subtitle>
        <v-divider class="mb-4"></v-divider>

        <!-- Número de póliza -->
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              :value="policyNumber"
              :disabled="loading"
              label="Número de póliza"
              prepend-inner-icon="mdi-shield-car"
              outlined
              dense
              hide-details="auto"
              @input="$emit('update:policyNumber', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Ficha técnica y Permiso de circulación -->
        <v-row>
          <v-col cols="12" md="6" v-for="section in sections" :key="section.title">
            <v-card outlined>
              <v-card-title class="py-2 subtitle-2">
                <v-icon left small color="primary">mdi-card-account-details-outline</v-icon>
                {{ section.title }}
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row dense>
                  <v-col cols="6" v-for="side in section.sides" :key="side.field">
                    <div class="caption grey--text mb-1">
                      <v-icon small class="mr-1">{{ side.icon }}</v-icon>{{ side.label }}
                    </div>
                    <div class="doc-slot">
                      <template v-if="previews[side.field]">
                        <div v-if="previews[side.field].isPdf"
                          class="text-center pa-3 grey lighten-4 rounded doc-placeholder">
                          <v-icon size="30" color="red darken-1">mdi-file-pdf-box</v-icon>
                          <div class="caption mt-1 text-truncate">{{ previews[side.field].name }}</div>
                        </div>
                        <v-img v-else :src="previews[side.field].src" height="110" contain
                          class="grey lighten-4 rounded"></v-img>
                        <v-btn small icon class="doc-remove-btn" :disabled="loading"
                          @click="removeFile(side.field)">
                          <v-icon small color="error">mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <div v-else class="text-center pa-3 grey--text grey lighten-4 rounded doc-placeholder"
                        style="cursor:pointer" @click="openSourceDialog(side.field)">
                        <v-icon size="30" color="grey lighten-1">mdi-camera-plus-outline</v-icon>
                        <div class="caption mt-1">Añadir</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Pop-up para elegir origen -->
    <v-dialog v-model="sourceDialog" max-width="340">
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left color="primary">mdi-camera-plus-outline</v-icon>
          Añadir documento
        </v-card-title>
        <v-divider></v-divider>
        <v-list>
          <v-list-item @click="pickFromCamera">
            <v-list-item-icon>
              <v-icon color="primary">mdi-camera</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Hacer foto</v-list-item-title>
              <v-list-item-subtitle>Usar la cámara del dispositivo</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="pickFromFiles">
            <v-list-item-icon>
              <v-icon color="primary">mdi-folder-image</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Subir archivo</v-list-item-title>
              <v-list-item-subtitle>Imagen o PDF desde tu dispositivo</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="sourceDialog = false">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Inputs ocultos -->
    <input ref="fileInput" type="file" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
      style="display:none" @change="onFileSelected">
    <input ref="cameraInput" type="file" accept="image/*" capture="environment"
      style="display:none" @change="onFileSelected">

    <div class="d-flex justify-space-between">
      <v-btn text @click="$emit('previous-step')">
        <v-icon left>mdi-arrow-left</v-icon>
        Anterior
      </v-btn>
      <v-btn color="success" @click="$emit('submit')" :loading="loading">
        <v-icon left>mdi-check</v-icon>
        Completar
      </v-btn>
    </div>
  </v-stepper-content>
</template>

<script>
export default {
  name: 'DocumentationStep',
  props: {
    policyNumber: {
      type: String,
      default: null
    },
    docFiles: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    stepNumber: {
      type: Number,
      default: 4
    }
  },
  data () {
    return {
      sourceDialog: false,
      pendingField: null,
      previews: {
        technical_sheet_front_image: null,
        technical_sheet_back_image: null,
        circulation_permit_front_image: null,
        circulation_permit_back_image: null
      },
      sections: [
        {
          title: 'Ficha técnica',
          sides: [
            { label: 'Parte delantera', field: 'technical_sheet_front_image', icon: 'mdi-camera-front' },
            { label: 'Parte trasera', field: 'technical_sheet_back_image', icon: 'mdi-camera-rear' }
          ]
        },
        {
          title: 'Permiso de circulación',
          sides: [
            { label: 'Parte delantera', field: 'circulation_permit_front_image', icon: 'mdi-camera-front' },
            { label: 'Parte trasera', field: 'circulation_permit_back_image', icon: 'mdi-camera-rear' }
          ]
        }
      ]
    }
  },
  methods: {
    openSourceDialog (field) {
      this.pendingField = field
      this.sourceDialog = true
    },
    pickFromCamera () {
      this.sourceDialog = false
      this.$refs.cameraInput.value = null
      this.$refs.cameraInput.click()
    },
    pickFromFiles () {
      this.sourceDialog = false
      this.$refs.fileInput.value = null
      this.$refs.fileInput.click()
    },
    onFileSelected (event) {
      const file = event.target.files && event.target.files[0]
      const field = this.pendingField
      if (!file || !field) return
      const isPdf = file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf')
      this.previews[field] = {
        src: isPdf ? null : URL.createObjectURL(file),
        isPdf,
        name: file.name
      }
      this.$emit('update:docFiles', { ...this.docFiles, [field]: file })
      this.pendingField = null
    },
    removeFile (field) {
      this.previews[field] = null
      this.$emit('update:docFiles', { ...this.docFiles, [field]: null })
    }
  }
}
</script>

<style scoped>
.doc-slot {
  position: relative;
}

.doc-placeholder {
  height: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.doc-remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: white;
}
</style>
